/**
 * Allows users to select a dimension and corresponding value for dimension+value coding pattern.
 * @agentic connections
 */

// Components
import { Group, Select, Button, Stack, Text } from "@mantine/core";
import type { ComboboxProps } from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";

/**
 * Single dimension-value pair entry.
 * @agentic connections
 */
type DimensionValueEntry = {
  dimension: string;
  value: string;
  valueDisplay?: string;
};

/**
 * Props for DimensionValueInput component.
 * @agentic connections
 */
type DimensionValueInputProps = {
  label: string;
  showLabel: boolean;
  dimensionOptions: Array<{ value: string; label: string }>;
  valueOptions: Record<string, Array<{ value: string; label: string }>>; // Keyed by dimension
  values: DimensionValueEntry[];
  onChange: (values: DimensionValueEntry[]) => void;
  description?: string;
  /** Forwarded to all inner Select components (e.g. to pin dropdown position). */
  comboboxProps?: ComboboxProps;
};

/**
 * Renders dimension/value select pairs with add/remove for eligibility, program, referralMethod fields.
 * @agentic connections
 */
export const DimensionValueInput = ({
  label,
  showLabel,
  dimensionOptions,
  valueOptions,
  values,
  onChange,
  description,
  comboboxProps,
}: DimensionValueInputProps) => {
  const addEntry = () => {
    onChange([...values, { dimension: "", value: "" }]);
  };

  const removeEntry = (index: number) => {
    onChange(values.filter((_, i) => i !== index));
  };

  const updateEntry = (
    index: number,
    field: "dimension" | "value",
    newValue: string,
  ) => {
    const updated = [...values];
    const entry = updated[index];

    if (field === "dimension") {
      entry.dimension = newValue;
      entry.value = ""; // Reset value when dimension changes
      entry.valueDisplay = undefined;
    } else {
      entry.value = newValue;
      const valueOption = valueOptions[entry.dimension]?.find(
        (opt) => opt.value === newValue,
      );
      entry.valueDisplay = valueOption?.label;
    }

    onChange(updated);
  };

  return (
    <Stack gap="xs">
      <Group justify={showLabel ? "space-between" : "flex-end"} align="center">
        {showLabel && (
          <Text size="sm" fw={500}>
            {label}
          </Text>
        )}
        {/* <Button
          size="xs"
          variant="light"
          leftSection={<IconPlus size={14} />}
          onClick={addEntry}
        >
          Add {label}
        </Button> */}
      </Group>
      {description && (
        <Text size="xs" c="dimmed">
          {description}
        </Text>
      )}
      {values.map((entry, index) => (
        <Group key={index} gap="xs" align="flex-start">
          <Select
            placeholder="Select dimension"
            data={dimensionOptions}
            value={entry.dimension}
            onChange={(value) =>
              value && updateEntry(index, "dimension", value)
            }
            style={{ flex: 1 }}
            comboboxProps={comboboxProps}
          />
          <Select
            placeholder="Select value"
            data={valueOptions[entry.dimension] || []}
            value={entry.value}
            onChange={(value) => value && updateEntry(index, "value", value)}
            disabled={!entry.dimension}
            style={{ flex: 1 }}
            comboboxProps={comboboxProps}
          />
          <Button
            size="xs"
            variant="subtle"
            color="red"
            onClick={() => removeEntry(index)}
          >
            <IconTrash size={14} />
          </Button>
        </Group>
      ))}
      <Button
        size="xs"
        variant="light"
        leftSection={<IconPlus size={14} />}
        onClick={addEntry}
        fullWidth
      >
        Add {label}
      </Button>
    </Stack>
  );
};
